import { APPOINTMENT_ID } from '@constants/storageKeys';
import {
  AppointmentAttribution,
  AppointmentType,
  LoginResponse,
  PartnerConnector,
} from '@generated/gql/graphql';
import { INTRODUCED_BY } from '@src/types/attribution';
import { BooleanSelection } from '@src/types/index';
import { execute } from '@utils/executeGql';
import { isNullish } from '@utils/isNullish';

import {
  SupportRemoteMutationPartnerInPerson,
  partnerLoginMutation,
} from './query';
import {
  GETTING_QUOTE,
  PartnerFormValues,
  SUPPORT_REMOTE,
  SupportRemoteAppointmentResult,
} from './types';

interface QueryResult {
  data: {
    partnerLogin: LoginResponse;
  };
  errors: [
    {
      message: string;
    },
  ];
}

const partnerLogin = (
  username: string,
  password: string
): Promise<QueryResult> => {
  return execute(partnerLoginMutation, {
    loginInput: {
      username,
      password,
    },
  });
};

const addSupportingToAppointment = async (
  token: string
): Promise<SupportRemoteAppointmentResult> => {
  const appointmentId = localStorage.getItem(APPOINTMENT_ID);
  return execute<SupportRemoteAppointmentResult, { id: string | null }>(
    SupportRemoteMutationPartnerInPerson,
    { id: appointmentId },
    token
  );
};

const resolveAppointmentType = (values: PartnerFormValues): AppointmentType => {
  // NOTE: support-remote should never reach here due to early return in handleOnSubmit
  if (
    values.appointmentType === SUPPORT_REMOTE ||
    values.appointmentType === GETTING_QUOTE
  ) {
    throw new Error(
      'support-remote should not be passed to resolveAppointmentType'
    );
  }
  if (
    values.appointmentType === AppointmentType.FaceToFace &&
    values.hasSupportingPartner === BooleanSelection.Yes &&
    values.supportingPartnerType === AppointmentType.PartnerRemote
  ) {
    return AppointmentType.FaceToFaceWithRemoteSupport;
  }
  if (
    values.appointmentType === AppointmentType.PartnerRemote &&
    values.hasSupportingPartner === BooleanSelection.Yes &&
    values.supportingPartnerType === AppointmentType.FaceToFace
  ) {
    return AppointmentType.PartnerRemoteWithFaceToFaceSupport;
  }

  return values.appointmentType;
};

const resolveAppointmentAttribution = (
  values: PartnerFormValues,
  connectors: Nullable<PartnerConnector[]>
): Nullable<AppointmentAttribution> => {
  const {
    introducedBy,
    customerReferral,
    connectorReferral,
    restoredQuoteAlreadyAttributed,
    restoreQuote,
  } = values;

  if (restoreQuote == BooleanSelection.Yes && restoredQuoteAlreadyAttributed) {
    return null;
  }

  switch (introducedBy) {
    case INTRODUCED_BY.PARTNER:
      // Should never reach here
      throw new Error('Partner referral not expected in Partner page');
    case INTRODUCED_BY.CUSTOMER: {
      if (!customerReferral) {
        return null;
      }

      return {
        referrer: {
          customer: {
            accountNumber: customerReferral,
          },
        },
      };
    }

    case INTRODUCED_BY.CONNECTOR: {
      if (!connectorReferral || isNullish(connectors)) {
        return null;
      }
      const listedConnector = connectors?.find(
        (connector) => connector.id === connectorReferral
      );

      // Manual typed in case list were empty
      if (!listedConnector) {
        return {
          connector: {
            id: connectorReferral,
            shortId: connectorReferral,
          },
        };
      }

      return {
        connector: {
          id: listedConnector.id,
          shortId: listedConnector.shortId,
        },
      };
    }
    case INTRODUCED_BY.NONE:
      return null;
    default:
      return null;
  }
};

const formatConnectorsOptions = (connectors: PartnerConnector[]) =>
  connectors.map((connector) => ({
    value: connector.id,
    label: `${connector.businessName} (${connector.shortId})`,
  }));

export {
  partnerLogin,
  addSupportingToAppointment,
  resolveAppointmentType,
  resolveAppointmentAttribution,
  formatConnectorsOptions,
};
